import { ExternalLink, Lock, Settings2 } from 'lucide-react'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useEvent } from '~/entities/event/model/use-events'
import { EventSectionEditButton } from '~/features/events/components/event-section-edit'
import { OccurrenceList } from '~/features/events/components/view/event-datetime-view'
import {
  EventFeatureRows,
  eventFeaturesSummary,
} from '~/features/events/components/view/event-features-view'
import {
  formatEventDay,
  formatEventLongDay,
  formatEventTimeRange,
} from '~/features/events/lib/event-date-format'
import { eventPriceLabel } from '~/features/events/lib/event-price-label'
import { ImageGallery } from '~/features/shared/components/image-gallery'
import { ClampedContent } from '~/features/shared/components/view/clamped-content'
import { DetailList, DetailRow } from '~/features/shared/components/view/detail-row'
import { TagBadges } from '~/features/shared/components/view/tags-display'
import * as m from '~/paraglide/messages'
import { countryDisplayName } from '~/shared/lib/countries'
import type { Event } from '~/shared/types'
import { Badge } from '~/shared/ui/badge'
import { Card } from '~/shared/ui/card'
import { Disclosure } from '~/shared/ui/disclosure'
import { RichTextContent } from '~/shared/ui/rich-text-content'
import { Skeleton } from '~/shared/ui/skeleton'

function mapsUrl(event: Event): string {
  const { coordinates, street, city, country } = event.location
  const query = coordinates
    ? `${coordinates.lat},${coordinates.lng}`
    : encodeURIComponent(`${street}, ${city}, ${country}`)
  return `https://www.google.com/maps/search/?api=1&query=${query}`
}

function EventWhen({ event }: { event: Event }) {
  const start = new Date(event.startTime)
  const end = new Date(event.endTime)
  const dates = event.occurrences.length
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-medium">{formatEventLongDay(start)}</span>
        <span className="text-muted-foreground">{formatEventTimeRange(start, end)}</span>
        {event.isCanceled && <Badge variant="destructive">{m.canceled()}</Badge>}
        {event.postponedOn && (
          <Badge variant="outline">
            {m.postponedTo()} {formatEventDay(new Date(event.postponedOn))}
          </Badge>
        )}
      </div>
      {dates > 1 && (
        <Disclosure title={m.eventDatesCount({ count: dates })}>
          <OccurrenceList occurrences={event.occurrences} />
        </Disclosure>
      )}
    </div>
  )
}

function EventWhere({ event }: { event: Event }) {
  const { name, street, city, country } = event.location
  return (
    <div className="space-y-0.5">
      {name && <p className="font-medium">{name}</p>}
      <p className="text-muted-foreground">
        {[street, city, countryDisplayName(country)].filter(Boolean).join(', ')}
      </p>
      <a
        href={mapsUrl(event)}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 font-medium text-brand-subtle-foreground hover:underline"
      >
        {m.openInMaps()}
        <ExternalLink className="size-3.5" />
      </a>
      {event.addressHidden ? (
        <p className="flex items-center gap-1.5 pt-1 text-muted-foreground text-xs">
          <Lock aria-hidden className="size-3.5 shrink-0" />
          {m.addressHiddenStatus()}
        </p>
      ) : null}
    </div>
  )
}

function EventPrice({ event }: { event: Event }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <span className="font-medium">{eventPriceLabel(event.price)}</span>
      {event.capacity ? (
        <span className="text-muted-foreground">
          {m.capacityAttendees({ count: event.capacity })}
        </span>
      ) : null}
      {event.ticketUrl ? (
        <a
          href={event.ticketUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 font-medium text-brand-subtle-foreground hover:underline"
        >
          {event.ticketUrlKind === 'registration' ? m.ticketLinkOpenSignup() : m.getTickets()}
          <ExternalLink className="size-3.5" />
        </a>
      ) : null}
    </div>
  )
}

function useLoadedEvent(eventId: string) {
  const { data: business } = useCurrentBusiness()
  const { data: event, isLoading } = useEvent(business?.id ?? '', eventId)
  return { event, loading: isLoading || !business }
}

export function EventDetailsCard({ eventId }: { eventId: string }) {
  const { event, loading } = useLoadedEvent(eventId)

  if (loading) return <Skeleton className="h-80 w-full rounded-xl" />
  if (!event) return null

  const images = event.images.map((image) => image.url)

  return (
    <Card className="p-4 sm:p-6">
      <h2 className="mb-4 font-semibold font-serif text-lg">{m.details()}</h2>
      <DetailList>
        <DetailRow
          label={m.description()}
          action={
            <EventSectionEditButton event={event} section="details" label={m.description()} />
          }
        >
          {event.description ? (
            <ClampedContent>
              <RichTextContent html={event.description} className="max-w-prose leading-relaxed" />
            </ClampedContent>
          ) : (
            <p className="text-muted-foreground">{m.eventDetailNoDescription()}</p>
          )}
        </DetailRow>
        <DetailRow
          label={m.dateTime()}
          action={<EventSectionEditButton event={event} section="datetime" label={m.dateTime()} />}
        >
          <EventWhen event={event} />
        </DetailRow>
        <DetailRow
          label={m.location()}
          action={<EventSectionEditButton event={event} section="location" label={m.location()} />}
        >
          <EventWhere event={event} />
        </DetailRow>
        <DetailRow
          label={m.price()}
          action={<EventSectionEditButton event={event} section="pricing" label={m.price()} />}
        >
          <EventPrice event={event} />
        </DetailRow>
        {event.tags?.category && (
          <DetailRow
            label={m.categories()}
            action={
              <EventSectionEditButton event={event} section="details" label={m.categories()} />
            }
          >
            <TagBadges
              categoryId={event.tags.category.id}
              subCategoryIds={event.tags.subCategories?.map((sub) => sub.id)}
            />
          </DetailRow>
        )}
        <DetailRow
          label={m.eventDetailGallery()}
          action={
            <EventSectionEditButton event={event} section="images" label={m.eventDetailGallery()} />
          }
        >
          {images.length > 0 ? (
            <ImageGallery
              images={images}
              label={event.title}
              className="grid-cols-5 gap-1.5 sm:grid-cols-6 lg:grid-cols-8"
            />
          ) : (
            <p className="text-muted-foreground">{m.eventDetailNoImages()}</p>
          )}
        </DetailRow>
      </DetailList>
    </Card>
  )
}

export function EventAdvancedCard({ eventId }: { eventId: string }) {
  const { event, loading } = useLoadedEvent(eventId)
  if (loading || !event) return null
  const { coordinates } = event.location

  return (
    <Card className="px-4 py-2 sm:px-6">
      <Disclosure
        title={m.moreSettingsTitle()}
        hint={eventFeaturesSummary(event)}
        icon={<Settings2 />}
        contentClassName="pb-4"
      >
        <DetailList>
          <DetailRow
            label={m.eventFeaturesTitle()}
            action={
              <EventSectionEditButton
                event={event}
                section="features"
                label={m.eventFeaturesTitle()}
              />
            }
          >
            <EventFeatureRows event={event} />
          </DetailRow>
          {coordinates && (
            <DetailRow
              label={m.coordinates()}
              action={
                <EventSectionEditButton event={event} section="location" label={m.coordinates()} />
              }
            >
              <span className="font-mono">
                {coordinates.lat.toFixed(6)}, {coordinates.lng.toFixed(6)}
              </span>
            </DetailRow>
          )}
        </DetailList>
      </Disclosure>
    </Card>
  )
}
